<template>
  <div ref="sectionRef" class="visit-inspection-section">
    <div class="visit-inspection-section__header" @click="expanded = !expanded">
      <div class="visit-inspection-section__title-container">
        <!-- <div
          v-if="sectionNumber !== undefined && sectionNumber !== null"
          class="visit-inspection-section__section-number"
        >
          {{ sectionNumber }}
        </div> -->
        <h2 class="visit-inspection-section__title">
          {{ title }}
        </h2>
      </div>
      <span class="visit-inspection-section__toggle-icon">
        <font-awesome-icon :icon="expanded ? 'chevron-up' : 'chevron-down'" />
      </span>
    </div>
    <div class="visit-inspection-section__content" v-show="expanded">
      <Checkbox
        v-if="showNAOption && hasNAOptionQuestions"
        class="visit-inspection-section__question-checkbox"
        v-model="isAllNotApplicable"
        label="Apply N/A to all questions in section"
        :disabled="!isAnswersModificationAllowed"
      />

      <!-- Previous Responses Section -->
      <div v-if="previousResponses.length > 0" class="visit-inspection-section__previous-responses">
        <div
          class="visit-inspection-section__previous-responses-header"
          @click="previousResponsesExpanded = !previousResponsesExpanded"
        >
          <h3 class="visit-inspection-section__previous-responses-title">
            Previous Responses ({{ new Date().getFullYear() }})
          </h3>
          <span class="visit-inspection-section__previous-responses-toggle-icon">
            <font-awesome-icon :icon="previousResponsesExpanded ? 'chevron-up' : 'chevron-down'" />
          </span>
        </div>
        <div v-show="previousResponsesExpanded" class="visit-inspection-section__previous-responses-content">
          <VisitInspectionCustomQuestion
            v-for="(response, i) in previousResponses"
            :key="i"
            v-model="previousResponses[i]"
            :questionNumber="getPreviousResponseQuestionNumber(response)"
            :disableSelection="true"
            :isPreviousResponse="true"
          />
        </div>
      </div>

      <slot></slot>

      <div v-if="visibleQuestions" class="visit-inspection-section__questions">
        <VisitInspectionCustomQuestion
          v-for="(answer, i) in visibleQuestions"
          v-show="props.showAnnualQuestions || !answer.IsAnnual"
          :key="i + '_' + answer.QuestionId"
          v-model="visibleQuestions[i]"
          :questionNumber="startQuestionNumber + i"
          :invalid="fieldIsInvalid('question-' + answer.QuestionId)"
          :disableSelection="!isAnswersModificationAllowed"
          @answerValueChanged="isAllNotApplicable = false"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { VisitInspectionAnswerDisplay } from "@/Domain/VisitInspectionAnswerDisplay";
import VisitInspectionCustomQuestion from "./VisitInspectionCustomQuestion.vue";
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, useId, watch, watchEffect } from "vue";
import { useInspectionStore } from "@/stores/useInspectionStore";
import { Checkbox } from "@progress/kendo-vue-inputs";
import { EnumVisitQuestionType } from "@/Domain/EnumVisitQuestionType";
import { useQuestionVisibility } from "@/composables/inspections/useQuestionVisibility";

const inspectionStore = useInspectionStore();
const { fieldIsInvalid } = inspectionStore;
const { isQuestionVisible } = useQuestionVisibility();

export interface Props {
  sectionNumber?: number;
  title: string;
  startQuestionNumber?: number;
  isAnswersModificationAllowed?: boolean;
  defaultExpanded?: boolean;
  showNAOption?: boolean;
  showAnnualQuestions?: boolean;
  additionalPreviousResponses?: VisitInspectionAnswerDisplay[];
}

const props = withDefaults(defineProps<Props>(), {
  startQuestionNumber: 1,
  isAnswersModificationAllowed: true,
  defaultExpanded: true,
  showNAOption: true,
  showAnnualQuestions: true,
});

const sectionRef = ref<HTMLElement | null>(null);

const questions = defineModel<VisitInspectionAnswerDisplay[]>();
const isAllNotApplicable = ref<boolean>(false);
const previousResponsesExpanded = ref(inspectionStore.previousResponsesExpanded);

const id = useId();
const expanded = computed({
  get: () => inspectionStore.sections[id]?.expanded ?? false,
  set: (val: boolean) => inspectionStore.addOrUpdateSection(id, props.sectionNumber, props.title, val),
});

const sortedQuestions = computed(() => {
  return questions.value?.slice().sort((a, b) => a.DisplayOrder - b.DisplayOrder) || [];
});

/**
 * Filtered questions based on visibility rules. Ignored in historical mode.
 */
const visibleQuestions = computed(() => {
  if (!props.isAnswersModificationAllowed) return sortedQuestions.value;
  return sortedQuestions.value.filter((q) => isQuestionVisible(q));
});

const previousResponses = computed(() => {
  const responses = sortedQuestions.value.filter((q) => q.IsAnnual).flatMap((q) => q.PreviousResponses ?? []);
  return [...responses, ...(props.additionalPreviousResponses ?? [])];
});

const hasNAOptionQuestions = computed(() => {
  return visibleQuestions.value?.some((q) => {
    return (
      (q.VisitQuestionType === EnumVisitQuestionType.RadioButton ||
        q.VisitQuestionType === EnumVisitQuestionType.Checkboxes) &&
      (q.Options?.length == 0 || q.Options?.includes("N/A"))
    );
  });
});

const getPreviousResponseQuestionNumber = function (response: VisitInspectionAnswerDisplay): number | undefined {
  const index = visibleQuestions.value.findIndex((q) => q.QuestionId === response.QuestionId) ?? -1;
  return index >= 0 ? (props.startQuestionNumber ?? 1) + index : undefined;
};

watchEffect(() => {
  inspectionStore.addOrUpdateSection(id, props.sectionNumber, props.title, expanded.value);
});

watch(
  () => inspectionStore.scrollToSectionId,
  (newId) => {
    if (newId === id) {
      inspectionStore.scrollToSectionId = "";
      setTimeout(() => sectionRef.value?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }
);

watch(isAllNotApplicable, (newValue) => {
  if (!newValue) return;

  // limit to only questions that allow N/A answer
  questions.value?.forEach((q) => {
    if (
      (q.VisitQuestionType === EnumVisitQuestionType.RadioButton ||
        q.VisitQuestionType === EnumVisitQuestionType.Checkboxes) &&
      (q.Options?.length == 0 || q.Options?.includes("N/A"))
    )
      q.AnswerValue = "N/A";
  });
});

watchEffect(() => {
  previousResponsesExpanded.value = inspectionStore.previousResponsesExpanded;
});

onBeforeMount(() => {
  expanded.value = props.defaultExpanded;
});

onMounted(() => {
  // Set up IntersectionObserver to track when section is in view
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        // Update the store with this section's visibility ratio
        // The store will determine which section should be active based on all visible sections
        if (entry.isIntersecting) {
          inspectionStore.updateSectionVisibility(id, entry.intersectionRatio);
        } else {
          inspectionStore.updateSectionVisibility(id, 0);
        }
      });
    },
    {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
    }
  );

  if (sectionRef.value) {
    observer.observe(sectionRef.value);
  }

  // Store the observer for cleanup
  onBeforeUnmount(() => {
    if (sectionRef.value) {
      observer.unobserve(sectionRef.value);
    }
    observer.disconnect();
    inspectionStore.removeSection(id);
  });
});
</script>

<style lang="scss" scoped>
.visit-inspection-section {
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;

  &__header {
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #f0f5fb;
  }

  &__title-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__section-number {
    background-color: #0056b3;
    color: #fff;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    color: #333;
  }

  &__toggle-icon {
    font-size: 16px;
    color: #666;
  }

  &__content {
    padding: 16px 4px;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    gap: 16px;

    @include media-breakpoint-up(sm) {
      padding: 16px 8px;
    }

    @include media-breakpoint-up(lg) {
      padding: 16px;
    }
  }

  &__previous-responses {
    margin-bottom: 12px;
    background-color: #fffacd; /* LemonChiffon */
    border-radius: 8px;
    border: 1px solid #dee2e6;
  }

  &__previous-responses-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 8px 12px;

    &:hover {
      opacity: 0.8;
    }
  }

  &__previous-responses-title {
    margin: 0;
    font-size: 16px;
    font-weight: bold;
    color: #495057;
  }

  &__previous-responses-toggle-icon {
    font-size: 14px;
    color: #666;
  }

  &__previous-responses-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
    padding: 4px;

    @include media-breakpoint-up(sm) {
      padding: 8px;
    }

    @include media-breakpoint-up(lg) {
      padding: 12px;
    }
  }

  &__questions {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
